import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle, EnvelopeSimple, ArrowClockwise, Code } from "@phosphor-icons/react";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") || "";
  const initialEmail = searchParams.get("email") || "";

  const [email, setEmail] = useState(initialEmail);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);

  const canAutoVerify = useMemo(() => Boolean(token), [token]);

  useEffect(() => {
    if (canAutoVerify) {
      verifyToken(token);
    }
  }, [canAutoVerify, token]);

  const verifyToken = async (verifyTokenValue) => {
    setVerifying(true);
    try {
      await apiClient.post("/auth/verify-email", { token: verifyTokenValue });
      setVerified(true);
      toast.success("Email verified successfully");
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message;
      toast.error(message || "Could not verify email");
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyClick = async () => {
    if (!token) {
      toast.error("Verification token missing from link");
      return;
    }
    await verifyToken(token);
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Enter your email");
      return;
    }

    setResending(true);
    try {
      const response = await apiClient.post("/auth/resend-verification", { email });
      if (response.data?.email_verified) {
        toast.success("Email is already verified. You can log in.");
      } else {
        toast.success("Verification email sent. Check your inbox.");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message;
      toast.error(message || "Could not resend verification");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="w-full max-w-md bg-surface border border-border/40 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/20 border border-primary/50 flex items-center justify-center">
            <Code weight="bold" className="w-6 h-6 text-primary" />
          </div>
          <h1 className="font-heading text-xl font-bold">DIIT Showcase</h1>
        </div>

        <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">Verify your email</h2>
        <p className="text-muted-foreground mb-6">
          Confirm your email to activate your account and start logging in.
        </p>

        {verified ? (
          <div className="rounded-xl border border-accent/40 bg-accent/10 p-4 mb-6">
            <div className="flex items-center gap-2 text-accent font-medium mb-1">
              <CheckCircle className="w-5 h-5" />
              Email verified
            </div>
            <p className="text-sm text-muted-foreground">Your account is ready. Continue to login.</p>
          </div>
        ) : (
          <>
            <Button
              type="button"
              onClick={handleVerifyClick}
              disabled={verifying || !token}
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full mb-5"
              data-testid="verify-email-button"
            >
              {verifying ? "Verifying..." : "Verify Email"}
            </Button>

            <form onSubmit={handleResend} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="resend-email" className="text-sm font-medium flex items-center gap-2">
                  <EnvelopeSimple className="w-4 h-4 text-primary" />
                  Resend verification email
                </Label>
                <Input
                  id="resend-email"
                  type="email"
                  placeholder="you@diit.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-background border-border focus:border-primary"
                  data-testid="resend-verification-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={resending}
                variant="outline"
                className="w-full h-12 rounded-full"
                data-testid="resend-verification-button"
              >
                <ArrowClockwise className="w-4 h-4 mr-2" />
                {resending ? "Sending..." : "Resend verification"}
              </Button>
            </form>
          </>
        )}

        <div className="mt-6 flex items-center justify-center gap-3">
          <Button asChild variant="outline" className="rounded-full" data-testid="verify-login-link">
            <Link to="/login">Go to Login</Link>
          </Button>
          {verified && (
            <Button
              type="button"
              className="rounded-full"
              onClick={() => navigate("/login", { replace: true })}
              data-testid="verify-continue-login"
            >
              Continue
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
